import { ChangeEvent, memo, useEffect, useState, FC } from "react";
import {
  Badge,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spacer,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";

import { DeviceInfo, TenantInfo } from "../../../types/api/tenantTableTypes";
import {
  SecurityLevelPtn,
  SecurityLevelType,
  TierPtn,
} from "../../../types/api/commonTypes";
import { PrimaryButton } from "../../atoms/button/PrimaryButton";
import { CloseIcon } from "@chakra-ui/icons";
import { useParameters } from "../../../hooks/useParameters";
import { DevicePtn } from "../../../types/api/parameterTypes";

type Props = {
  device: DeviceInfo;
  isOpen: boolean;
  onInsert: (
    deviceName: string,
    securityLevel: SecurityLevelType,
    lfourId: number[],
    binaryStorageSecurityLevel?: 'global' | 'public'
  ) => void;
  onUpdate: (
    deviceName: string,
    securityLevel: SecurityLevelType,
    lfourId: number[],
    binaryStorageSecurityLevel?: 'global' | 'public'
  ) => void;
  onDelete: (deviceName: string) => void;
  onClose: () => void;
  tenant?: TenantInfo;
};

export const DeviceDetailModal: FC<Props> = memo((props) => {
  const { isOpen, onClose, device, tenant } = props;
  const isAddMode: boolean = device.DeviceName ? false : true;
  const { getDevicePtn, getDeviceNames, deviceNames } = useParameters();

  const [deviceName, setDeviceName] = useState("");
  const [devicePtn, setDevicePtn] = useState<DevicePtn|undefined>(undefined);
  const [securityLevel, setSecurityLevel] = useState<SecurityLevelType>("");
  const [lfourId, setLfourId] = useState(""); // 編集中のlfourID
  const [lfourIds, setLfourIds] = useState<number[]>([]);
  const [binaryStorageSecurityLevel, setBinaryStorageSecurityLevel] = useState<'global' | 'public' | ''>('');

  useEffect(() => {
    getDeviceNames();
  }, []);

  useEffect(() => {
    onModalReset();
  }, [device]);

  const onChangeDeviceName = (e: ChangeEvent<HTMLSelectElement>) => {
    const newDeviceName = e.target.value;
    setDeviceName(newDeviceName);
    setDevicePtn(getDevicePtn(newDeviceName));
  };
  const onChangeSecurityLevel = (e: ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === SecurityLevelPtn.Public) {
      setSecurityLevel(SecurityLevelPtn.Public);
    } else if (e.target.value === SecurityLevelPtn.Private) {
      setSecurityLevel(SecurityLevelPtn.Private);
    } else if (e.target.value === SecurityLevelPtn.Exclusive) {
      setSecurityLevel(SecurityLevelPtn.Exclusive);
    } else if (e.target.value === SecurityLevelPtn.Anonymized) {
      setSecurityLevel(SecurityLevelPtn.Anonymized);
    } else {
      setSecurityLevel("");
    }
  };
  const onChangeLfourId = (e: ChangeEvent<HTMLInputElement>) =>
    setLfourId(e.target.value);
  
  const onChangeBinaryStorageSecurityLevel = (e: ChangeEvent<HTMLSelectElement>) => {
    setBinaryStorageSecurityLevel(e.target.value as 'global' | 'public' | '');
  };

  const onClickLfourIdAdd = () => {
    if (lfourId === "") return;
    setLfourIds([...lfourIds, Number(lfourId)]);
    setLfourId("");
  };

  const onClickListDelete = (index: number) => {
    const newLfourIds = [...lfourIds];
    newLfourIds.splice(index, 1);
    setLfourIds(newLfourIds);
  };

  // 配列中の重複削除＆昇順ソート
  const onClickSort = () => {
    const newLfourIds = [...lfourIds].filter(function (x, i, self) {
      return self.indexOf(Number(x)) === i;
    });
    newLfourIds.sort((a, b) => {
      return a - b;
    });
    setLfourIds(newLfourIds);
  };

  const onClickUpdate = () => {
    props.onUpdate(deviceName, securityLevel, lfourIds, binaryStorageSecurityLevel || undefined);
    props.onClose();
  };

  const onClickCreate = () => {
    props.onInsert(deviceName, securityLevel, lfourIds, binaryStorageSecurityLevel || undefined);
    props.onClose();
  };

  const onClickDelete = () => {
    // SSMパラメータに存在しない場合でも、device.DeviceNameを使用して削除できるようにする
    const deviceNameToDelete = deviceName || device.DeviceName;
    if (deviceNameToDelete) {
      props.onDelete(deviceNameToDelete);
      props.onClose();
    }
  };

  const onModalReset = () => {
    setLfourId("");
    const devName = device.DeviceName ?? "";
    setDeviceName(devName);
    setDevicePtn(getDevicePtn(devName));
    setSecurityLevel(device.SecurityLevel);
    setBinaryStorageSecurityLevel((device as any).binaryStorageSecurityLevel ?? '');
    if (device.lfourIds) {
      setLfourIds(device.lfourIds);
    } else {
      setLfourIds([]);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onModalReset();
        onClose();
      }}
      motionPreset="slideInBottom"
      autoFocus={false}
    >
      <ModalOverlay />
      <ModalContent pb={2}>
        <ModalHeader>ドライバ詳細</ModalHeader>
        <ModalCloseButton />
        <ModalBody mx={{ base: 0, md: 6 }}>
          <Stack spacing={4}>
            <FormControl>
              <FormLabel>
                ドライバの種類
                {isAddMode ? (
                  <Badge colorScheme="red" ml={2}>
                    必須
                  </Badge>
                ) : (
                  <Badge colorScheme="gray" ml={2}>
                    変更不可
                  </Badge>
                )}
              </FormLabel>
              <Select value={deviceName} onChange={onChangeDeviceName}>
                {isAddMode ? (
                  <>
                    <option value={""}>未選択</option>
                    {deviceNames.map((name) => (
                      <option value={name}>{name}</option>
                    ))}
                  </>
                ) : (
                  <option value={deviceName}>{deviceName}</option>
                )}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>セキュリティレベル</FormLabel>
              <Select value={securityLevel} onChange={onChangeSecurityLevel}>
                <option value={""}>未設定</option>
                <option value={SecurityLevelPtn.Public}>IoT(public)</option>
                <option value={SecurityLevelPtn.Private}>IoT(private)</option>
                <option value={SecurityLevelPtn.Exclusive}>IoT(exclusive)</option>
                <option value={SecurityLevelPtn.Anonymized}>
                  匿名加工情報
                </option>
                {tenant?.tier === TierPtn.v2 ? (
                  <>
                    <option value={SecurityLevelPtn.Related}>
                      個人関連情報
                    </option>
                    <option value={SecurityLevelPtn.Pseudonymized}>
                      仮名加工情報
                    </option>
                  </>
                ) : (
                  <></>
                )}
              </Select>
            </FormControl>
            {devicePtn?.binaryStorage && (
              <FormControl>
                <FormLabel>
                  バイナリデータストレージ：セキュリティレベル
                  <Badge colorScheme="red" ml={2}>
                    必須
                  </Badge>
                </FormLabel>
                <Select value={binaryStorageSecurityLevel} onChange={onChangeBinaryStorageSecurityLevel}>
                  <option value="">未選択</option>
                  <option value="global">global（認証なし）</option>
                  <option value="public">public（APIキー認証）</option>
                </Select>
              </FormControl>
            )}
            <FormControl>
              <FormLabel>LfourID（10進数）</FormLabel>
              <HStack>
                <Input
                  type={"number"}
                  value={lfourId}
                  placeholder="LfourID"
                  onChange={onChangeLfourId}
                />
                <PrimaryButton onClick={onClickLfourIdAdd}>追加</PrimaryButton>
                <Spacer />
                <PrimaryButton onClick={onClickSort}>ID整理</PrimaryButton>
              </HStack>
            </FormControl>
            <Flex border={"solid"} borderWidth={1} borderColor={"gray.300"}>
              <TableContainer>
                <Table size={"sm"}>
                  <Thead>
                    <Tr>
                      <Th>LfourID（10進数）</Th>
                      <Th>（16進数）</Th>
                      <Th textAlign={"center"}>削除</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {lfourIds.map((cell, i) => (
                      <Tr key={i}>
                        <Td>{cell}</Td>
                        <Td>
                          {Number(String(cell)).toString(16).toUpperCase()}
                        </Td>
                        <Td textAlign={"center"}>
                          <IconButton
                            onClick={() => onClickListDelete(i)}
                            variant="outline"
                            colorScheme="red"
                            aria-label="削除"
                            size={"xs"}
                            icon={<CloseIcon />}
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Flex>
            <Spacer />
          </Stack>
        </ModalBody>
        <ModalFooter>
          <HStack>
            {isAddMode ? (
              <PrimaryButton
                disabled={deviceName === "" || (devicePtn?.binaryStorage && binaryStorageSecurityLevel === "")}
                onClick={onClickCreate}
              >
                追加
              </PrimaryButton>
            ) : (
              <>
                <PrimaryButton 
                  onClick={onClickUpdate} 
                  disabled={!deviceName || (devicePtn?.binaryStorage && binaryStorageSecurityLevel === "")}
                >
                  更新
                </PrimaryButton>
                <PrimaryButton onClick={onClickDelete}>削除</PrimaryButton>
              </>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});
